import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import backend from "../../../../../api";
import { socket } from "../../../../../api/Socket";
import { startConversation } from "../../../../../utils/startConversation";
import { uploadMedia } from "../../../../../utils/uploadMedia";
import Classes from "./Messages.module.css";
import LinearProgress from "@mui/material/LinearProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";

/* ───────── motion variants ───────── */
const listVariants = {
  initial: { opacity: 0, x: -10 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.25 } },
  exit: { opacity: 0, x: -10, transition: { duration: 0.25 } },
};
const chatVariants = {
  initial: { opacity: 0, x: 10 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: 10, transition: { duration: 0.3 } },
};

/* ───────── helpers ───────── */
const getExt = (url = "") => {
  try {
    const path = decodeURIComponent(String(url).split("?")[0] || "");
    const parts = path.split("/");
    const name = parts[parts.length - 1] || "";
    const dot = name.lastIndexOf(".");
    return { ext: dot >= 0 ? name.slice(dot + 1).toLowerCase() : "", name };
  } catch {
    return { ext: "", name: "download" };
  }
};
const isImage = (ext) => ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);
const isVideo = (ext) => ["mp4", "webm", "ogg", "mov", "m4v"].includes(ext);
const isDoc   = (ext) => ["pdf", "csv", "xls", "xlsx", "txt"].includes(ext);

const classifyMediaSnippet = (mediaUrl, own) => {
  if (!mediaUrl) return null;
  const { ext } = getExt(mediaUrl);
  const kind = isImage(ext) ? "Image" : isVideo(ext) ? "Video" : isDoc(ext) ? "File" : "File";
  return `${kind} ${own ? "sent" : "received"}`;
};

// Parse "YYYY-MM-DD at HH:mm" to Date
const parseStartsInToDate = (s) => {
  if (!s) return null;
  const cleaned = String(s).trim();
  const [datePart, timePart = "00:00"] = cleaned.split(" at ").map((x) => (x || "").trim());
  const [y, m, d] = (datePart || "").split("-").map(Number);
  const [hh, mm] = (timePart || "").split(":").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0);
};

// "MM/DD/YYYY at h:mm"
const formatCompact12h = (dt) => {
  if (!dt || isNaN(dt.getTime())) return "";
  const pad2 = (n) => String(n).padStart(2, "0");
  const M = pad2(dt.getMonth() + 1);
  const D = pad2(dt.getDate());
  const Y = dt.getFullYear();
  const h12 = (dt.getHours() % 12) || 12;
  const minutes = pad2(dt.getMinutes());
  return `${M}/${D}/${Y} at ${h12}:${minutes}`;
};

/* ───────── attachment renderer ───────── */
const Attachment = ({ url }) => {
  if (!url) return null;
  const { ext, name } = getExt(url);

  const box = {
    border: "1px solid var(--border, #e6e6e6)",
    borderRadius: 10,
    padding: "8px 10px",
    display: "inline-flex",
    flexDirection: "column",
    gap: 8,
    maxWidth: 280,
    background: "var(--bg, #fff)",
  };
  const row = { display: "flex", alignItems: "center", gap: 8 };
  const iconCircle = {
    width: 32, height: 32, borderRadius: 8, display: "inline-flex",
    alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", background: "#f5f5f5", minWidth: 32,
  };
  const nameStyle = { fontSize: 12, lineHeight: 1.2, wordBreak: "break-word", color: "inherit" };
  const btn = { fontSize: 12, padding: "4px 8px", border: "1px solid #ddd", borderRadius: 6, background: "transparent", cursor: "pointer" };

  if (isImage(ext)) {
    return (
      <div style={box}>
        <img src={url} alt={name} style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: 8 }} />
        <div style={row}>
          <div style={iconCircle}>img</div>
          <div style={{ flex: 1 }}><div style={nameStyle}>{name}</div></div>
          <a href={url} download target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
            <button style={btn}>Download</button>
          </a>
        </div>
      </div>
    );
  }
  if (isVideo(ext)) {
    return (
      <div style={box}>
        <video src={url} controls style={{ width: "100%", maxHeight: 240, borderRadius: 8 }} />
        <div style={row}>
          <div style={iconCircle}>vid</div>
          <div style={{ flex: 1 }}><div style={nameStyle}>{name}</div></div>
          <a href={url} download target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
            <button style={btn}>Save</button>
          </a>
        </div>
      </div>
    );
  }
  const label = isDoc(ext) ? ext : (ext || "file");
  return (
    <div style={box}>
      <div style={row}>
        <div style={iconCircle}>{label}</div>
        <div style={{ flex: 1 }}><div style={nameStyle}>{name}</div></div>
        <a href={url} download target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
          <button style={btn}>Download</button>
        </a>
      </div>
    </div>
  );
};

/* ───────── chat items ───────── */
const ChatMessage = ({ own, text, time, media }) => (
  <div className={`${Classes.msgRow} ${own ? Classes.own : Classes.other}`}>
    <div className={`${Classes.bubble} ${own ? Classes.ownB : Classes.otherB}`}>
      {text}
      {media && (
        <div className={Classes.media} style={{ marginTop: text ? 10 : 0 }}>
          <Attachment url={media} />
        </div>
      )}
    </div>
    <span className={Classes.msgTime}>{time}</span>
  </div>
);

// Inline booking chips
const BookingRow = ({ count, nextLabel, status }) => {
  const wrap = { display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" };
  const pill = (bg, color, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 8px",
    fontSize: 11,
    borderRadius: 999,
    background: bg,
    color,
    border: `1px solid ${border}`,
  });
  const statusColor = (() => {
    const s = (status || "").toLowerCase();
    if (s === "paid" || s === "accepted") return { bg: "rgba(36,161,72,0.10)", color: "#156c2f", border: "#bdecc8" };
    if (s === "canceled") return { bg: "rgba(224,49,49,0.10)", color: "#7a1c1c", border: "#f1b0b0" };
    return { bg: "rgba(250,176,5,0.10)", color: "#895f14", border: "#f4dda5" }; // Pending
  })();

  return (
    <div style={wrap}>
      <span style={pill("#eef2ff", "#3730a3", "#c7d2fe")}>Bookings: <strong>{count}</strong></span>
      {nextLabel ? <span style={pill("#ecfeff", "#155e75", "#a5f3fc")}>Next: <strong>{nextLabel}</strong></span> : null}
      {/* {status ? <span style={pill(statusColor.bg, statusColor.color, statusColor.border)}>{status}</span> : null} */}
    </div>
  );
};

/* ───────── list & window ───────── */
const ContactList = ({ list, activeId, onSelect, isMobile, loading }) => {
  const [query, setQuery] = useState("");
  const filtered = list.filter((c) => (c.name || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <motion.aside className={Classes.aside} variants={listVariants} initial="initial" animate="animate" exit="exit">
      <header className={Classes.asideHeader}>Your Mentees</header>

      <div className={Classes.searchWrap}>
        <input
          className={Classes.search}
          placeholder="Search mentee"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className={Classes.contactList} style={{ position: "relative", minHeight: 120 }}>
        {loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </div>
        )}
        {!loading && (filtered.length === 0 ? (
          <h5 className="placeholderText" style={{ margin: "1rem 1.2rem" }}>No conversations yet…</h5>
        ) : (
          filtered.map((c) => (
            <button
              key={c.id}
              className={`${Classes.contactItem} ${c.conversationId === activeId ? Classes.active : ""}`}
              onClick={() => onSelect(c.id, c.conversationId)}
            >
              <img src={c.avatar} alt="" className={Classes.avatar} draggable={false} />
              <div className={Classes.meta}>
                <h5 style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>{c.name}</span>
                  {/* unread badge */}
                  {c.unread > 0 && (
                    <span
                      style={{
                        background: "#f44336",
                        color: "#fff",
                        borderRadius: 999,
                        fontSize: 11,
                        minWidth: 18,
                        height: 18,
                        lineHeight: "18px",
                        textAlign: "center",
                        padding: "0 6px",
                      }}
                    >
                      {c.unread}
                    </span>
                  )}
                </h5>
                <p
                  className={Classes.snippet}
                  style={{ fontWeight: c.unread > 0 ? 700 : 400 }}
                >
                  {c.snippet || "Start conversation"}
                </p>
                <span className={Classes.time}>{c.time}</span>
                <BookingRow count={c.bookingCount || 0} nextLabel={c.nextBookingLabel} status={c.lastStatus} />
              </div>
            </button>
          ))
        ))}
      </div>
    </motion.aside>
  );
};

const ChatWindow = ({ contact, send, back, isMobile, uploading, uploadProgress, sending }) => {
  const bottomRef = useRef(null);
  const [text, setText] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [contact?.messages]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !contact) return;
    try {
      const mediaUrl = await uploadMedia(files, null, (p) => uploadProgress.set(p));
      if (mediaUrl) {
        await send(contact.id, text.trim(), mediaUrl);
        setText("");
      }
    } catch (err) {
      console.error("Failed to upload media:", err.message);
    } finally {
      uploadProgress.set(0);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSend = () => {
    if (!contact) return;
    const hasText = !!text.trim();
    const hasFiles = !!fileInputRef.current?.files?.length;
    if (!hasText && !hasFiles) return;

    if (hasText && !hasFiles) {
      send(contact.id, text.trim());
      setText("");
    }
    if (hasFiles) {
      handleFileUpload({ target: fileInputRef.current });
    }
  };

  if (!contact) {
    return (
      <motion.section className={Classes.chatBlank} key="blank" variants={chatVariants} initial="initial" animate="animate" exit="exit">
        <div className={Classes.emptyMessage}>
          <img src="/assets/images/empty-message.png" draggable={false} alt="no-messages" />
        </div>
      </motion.section>
    );
  }

  // header chips
  const headRow = { display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexWrap: "wrap" };
  const pill = {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "2px 8px", fontSize: 11, borderRadius: 999,
    background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe",
  };

  return (
    <motion.section className={Classes.chat} key={contact.id} variants={chatVariants} initial="initial" animate="animate" exit="exit">
      <header className={Classes.chatHeader}>
        {isMobile && (
          <button className={Classes.backBtn} onClick={back} title="Back">
            <svg xmlns="http://www.w3.org/2000/svg" height="15" viewBox="0 0 448 512">
              <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
            </svg>
          </button>
        )}
        <img src={contact.avatar} alt="" className={Classes.avatar} draggable={false} />
        <h4>{contact.name}</h4>

        <div style={headRow}>
          <span style={pill}>Bookings: <strong>{contact.bookingCount || 0}</strong></span>
          {contact.nextBookingLabel ? (
            <span style={{ ...pill, background: "#ecfeff", color: "#155e75", border: "1px solid #a5f3fc" }}>
              Next: <strong>{contact.nextBookingLabel}</strong>
            </span>
          ) : null}
          {/* {contact.lastStatus ? (
            <span style={{ ...pill, background: "rgba(250,176,5,0.10)", color: "#895f14", border: "1px solid #f4dda5" }}>
              {contact.lastStatus}
            </span>
          ) : null} */}
        </div>
      </header>

      {(uploading || sending) && (
        <div style={{ padding: "4px 12px" }}>
          <LinearProgress variant={uploading ? "determinate" : "indeterminate"} value={uploading ? uploadProgress.value : undefined} />
        </div>
      )}

      <div className={Classes.chatBody}>
        {contact.messages.map((m) => (
          <ChatMessage key={m.id} own={m.own} text={m.text} time={m.time} media={m.media} />
        ))}
        <div ref={bottomRef} />
      </div>

      <footer className={Classes.composer}>
        <div className={Classes.inputWrap}>
          <button className={Classes.sendBtn} onClick={() => fileInputRef.current?.click()} disabled={uploading || sending} title="Attach file">+</button>
          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
          <input
            className={Classes.input}
            placeholder="Type your message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            disabled={uploading || sending}
          />
          <button className={Classes.sendBtn} onClick={handleSend} disabled={uploading || sending} title="Send">➤</button>
        </div>
      </footer>
    </motion.section>
  );
};

/* ───────── root (mentor side) ───────── */
export default function Messages() {
  const { user } = useSelector((s) => s.auth);
  const location = useLocation();
  const navigate = useNavigate();

  // contacts: booked mentees with messages, plus currently active (even if empty/new)
  const [contacts, setContacts] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // loading / init states
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingConvos, setLoadingConvos] = useState(true);
  const [initializing, setInitializing] = useState(true); // overall init spinner
  const [forcingOpen, setForcingOpen] = useState(false);  // while opening via URL
  const [uploading, setUploading] = useState(false);
  const [uploadProgressVal, setUploadProgressVal] = useState(0);
  const uploadProgress = { value: uploadProgressVal, set: setUploadProgressVal };
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 600 : false
  );

  // booked mentees map: menteeId -> { name, avatar, bookingCount, nextBookingLabel, lastStatus }
  const bookedMapRef = useRef(new Map());
  const hasOpenedInitialChat = useRef(false);

  /* responsive watcher */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Socket lifecycle (status toasts optional)
  useEffect(() => {
    const onConnect = () => setSnack({ open: true, message: "Connected", severity: "success" });
    const onDisconnect = () => setSnack({ open: true, message: "Disconnected. Reconnecting…", severity: "warning" });
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.emit("join", user._id.toString());
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [user._id]);

  // 1) Fetch bookings (mentor side) -> fill bookedMapRef
  useEffect(() => {
    let cancelled = false;
    const fetchBookings = async () => {
      setLoadingBookings(true);
      try {
        const { data } = await backend.get(`/bookings/mentor?mentorId=${user._id}`);
        const bookings = Array.isArray(data) ? data : [];
        const byMentee = new Map();
        const now = new Date();

        bookings.forEach((b) => {
          const menteeId =
            (b.menteeId || b.userId || b.user_id || (b.user?._id) || "").toString() || "";
          if (!menteeId) return;
          const arr = byMentee.get(menteeId) || [];
          arr.push(b);
          byMentee.set(menteeId, arr);
        });

        const context = new Map();
        byMentee.forEach((arr, menteeId) => {
          const name = arr[0]?.menteeName || "Unknown User";
          const avatar = "https://i.pravatar.cc/80";
          const upcoming = arr
            .map((b) => ({ dt: parseStartsInToDate(b.startsIn), status: b?.status?.label }))
            .filter(({ dt }) => dt && dt >= now)
            .sort((a, b) => a.dt - b.dt);
          const next = upcoming[0]?.dt || null;
          const nextLabel = next ? formatCompact12h(next) : null;
          const lastStatus = (arr[arr.length - 1]?.status?.label) || null;

          context.set(menteeId, {
            name,
            avatar,
            bookingCount: arr.length,
            nextBookingLabel: nextLabel,
            lastStatus,
          });
        });

        if (!cancelled) {
          bookedMapRef.current = context;
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Fetch mentor bookings error:", err.message);
          setError("Failed to fetch bookings: " + err.message);
          // setSnack({ open: true, message: "Failed to fetch bookings", severity: "error" });
          bookedMapRef.current = new Map();
        }
      } finally {
        !cancelled && setLoadingBookings(false);
      }
    };
    fetchBookings();
    return () => { cancelled = true; };
  }, [user._id]);

  // helper: compute unread count for a conversation
  const computeUnread = useCallback(async (conversationId) => {
    try {
      const { data } = await backend.get(`/api/chat/messages/${conversationId}`);
      const msgs = data?.data || [];
      const unread = msgs.filter((m) => !m.seen && m.sender?.toString?.() !== user._id.toString()).length;
      const last = msgs[msgs.length - 1];
      return {
        unread,
        lastActivity: last ? new Date(last.createdAt).getTime() : 0,
      };
    } catch {
      return { unread: 0, lastActivity: 0 };
    }
  }, [user._id]);

  // helper: mark all incoming messages in a convo as seen (DB + local)
  const markConversationSeen = useCallback(async (conversationId) => {
    try {
      await backend.post("/api/chat/messages/mark-seen", {
        conversationId,
        userId: user._id.toString(),
      });
      // zero unread locally
      setContacts((p) =>
        p.map((c) => (c.conversationId === conversationId ? { ...c, unread: 0 } : c))
      );
    } catch (err) {
      console.error("mark-seen failed:", err?.message);
    }
  }, [user._id]);

  // 2) Fetch conversations and merge with booked map; also pull unread counts, then sort
  useEffect(() => {
    let cancelled = false;

    const fetchConvos = async () => {
      setLoadingConvos(true);
      try {
        const { data } = await backend.get(`/api/chat/conversation?userId=${user._id.toString()}`);
        const conversations = data?.data || [];
        const context = bookedMapRef.current;

        // Keep only booked mentees
        let list = conversations
          .filter((c) => context.has(c.partnerId?.toString?.() || ""))
          .map((c) => {
            const menteeId = c.partnerId.toString();
            const ctx = context.get(menteeId) || {};
            const own = c.last?.sender?.toString?.() === user._id?.toString?.();
            const mediaSnippet = c.last?.media ? classifyMediaSnippet(c.last.media, own) : null;

            return {
              id: menteeId,
              name: c.partnerName || ctx.name || "Unknown",
              avatar: c.partnerAvatar || ctx.avatar || "https://i.pravatar.cc/80",
              conversationId: c._id?.toString?.() || null,
              snippet: (mediaSnippet || c.last?.text || ""),
              time: c.last
                ? new Date(c.last.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : "",
              messages: [],
              bookingCount: ctx.bookingCount || 0,
              nextBookingLabel: ctx.nextBookingLabel || null,
              lastStatus: ctx.lastStatus || null,
              hasLast: !!c.last,
              unread: 0,            // will fill below
              lastActivity: c.last ? new Date(c.last.createdAt).getTime() : 0,
            };
          })
          .filter((x) => x.hasLast);

        // fetch unread counts in parallel (limit to avoid stampede if needed)
        const withUnread = await Promise.all(
          list.map(async (c) => {
            if (!c.conversationId) return c;
            const stat = await computeUnread(c.conversationId);
            return { ...c, unread: stat.unread, lastActivity: stat.lastActivity || c.lastActivity };
          })
        );

        if (cancelled) return;

        // sort: unread first, then by lastActivity desc
        withUnread.sort((a, b) => {
          if ((a.unread || 0) > 0 && (b.unread || 0) === 0) return -1;
          if ((b.unread || 0) > 0 && (a.unread || 0) === 0) return 1;
          return (b.lastActivity || 0) - (a.lastActivity || 0);
        });

        setContacts(withUnread);
      } catch (err) {
        if (!cancelled) {
          console.error("Fetch conversations error:", err.message);
          setError("Failed to fetch conversations: " + err.message);
          setSnack({ open: true, message: "Failed to fetch conversations", severity: "error" });
        }
      } finally {
        !cancelled && setLoadingConvos(false);
      }
    };

    // only after bookings are ready (to avoid “No booking found” race)
    if (!loadingBookings) fetchConvos();
    return () => { cancelled = true; };
  }, [user._id, loadingBookings, computeUnread]);

  // 3) Open or create chat for ?userId= once bookings are ready
  const openChat = useCallback(
    async (partnerId, conversationId) => {
      try {
        // sync URL param
        const params = new URLSearchParams(location.search);
        params.set("userId", partnerId);
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });

        if (conversationId) {
          setActiveId(conversationId);
          // mark seen now (async)
          await markConversationSeen(conversationId);
          return;
        }

        // Ensure mentee is booked
        const ctx = bookedMapRef.current.get(partnerId);
        if (!ctx) {
          setSnack({ open: true, message: "No booking found with this user.", severity: "warning" });
          return;
        }

        setForcingOpen(true);
        const convo = await startConversation(user._id.toString(), partnerId.toString());
        setActiveId(convo._id.toString());

        setContacts((p) => {
          const exists = p.some((c) => c.id === partnerId);
          const base = exists ? p.filter((c) => c.id !== partnerId) : p;
          return [
            {
              id: partnerId,
              name: ctx.name || "Unknown",
              avatar: ctx.avatar || "https://i.pravatar.cc/80",
              conversationId: convo._id.toString(),
              snippet: "", time: "", messages: [],
              bookingCount: ctx.bookingCount || 0,
              nextBookingLabel: ctx.nextBookingLabel || null,
              lastStatus: ctx.lastStatus || null,
              unread: 0,
              lastActivity: Date.now(),
            },
            ...base,
          ];
        });

        await markConversationSeen(convo._id.toString());
        setError(null);
      } catch (err) {
        console.error("Open chat error:", err.message);
        setError("Failed to start conversation: " + err.message);
        setSnack({ open: true, message: "Failed to start conversation", severity: "error" });
      } finally {
        setForcingOpen(false);
      }
    },
    [user._id, location.pathname, location.search, navigate, markConversationSeen]
  );

  // Initial open via ?userId=
  useEffect(() => {
    if (hasOpenedInitialChat.current || loadingBookings) return;
    const params = new URLSearchParams(location.search);
    const targetUserId = params.get("userId");
    if (!targetUserId) return;

    const ctx = bookedMapRef.current.get(targetUserId);
    if (!ctx) {
      setSnack({ open: true, message: "No booking found with this user.", severity: "warning" });
      hasOpenedInitialChat.current = true;
      return;
    }

    const existing = contacts.find((c) => c.id === targetUserId);
    openChat(targetUserId, existing?.conversationId || null);
    hasOpenedInitialChat.current = true;
  }, [loadingBookings, contacts, location.search, openChat]);

  // overall initializing spinner
  useEffect(() => {
    setInitializing(loadingBookings || loadingConvos || forcingOpen);
  }, [loadingBookings, loadingConvos, forcingOpen]);

  /* Load messages for active conversation (then mark seen) */
  useEffect(() => {
    const active = contacts.find((c) => c.conversationId === activeId);
    if (!active || !active.conversationId || active.messages.length) {
      // still mark as seen if we navigated to it with previous messages
      if (active && active.conversationId) markConversationSeen(active.conversationId);
      return;
    }

    backend
      .get(`/api/chat/messages/${active.conversationId}`)
      .then(async ({ data }) => {
        const msgs = data?.data || [];
        setContacts((p) =>
          p.map((c) =>
            c.conversationId === activeId
              ? {
                  ...c,
                  messages: msgs.map((m) => ({
                    id: m._id.toString(),
                    own: m.sender.toString() === user._id.toString(),
                    text: m.text,
                    time: new Date(m.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                    media: m.media,
                  })),
                }
              : c
          )
        );
        await markConversationSeen(active.conversationId);
      })
      .catch((err) => {
        console.error("Fetch messages error:", err.message);
        setError("Failed to fetch messages: " + err.message);
        setSnack({ open: true, message: "Failed to fetch messages", severity: "error" });
      });
  }, [activeId, contacts, user._id, markConversationSeen]);

  /* Socket inbound: update unread, sorting, and auto-mark-seen if active */
  useEffect(() => {
    const handler = async (msg) => {
      if (!msg?.conversationId || !msg?._id) return;
      const convId = msg.conversationId.toString();
      const fromMe = msg.sender?.toString?.() === user._id?.toString?.();

      setContacts((p) => {
        // Ensure item exists
        const idx = p.findIndex((c) => c.conversationId === convId);
        if (idx === -1) return p; // we only list booked+convo users

        const c = p[idx];
        const own = fromMe;
        const mediaSnippet = classifyMediaSnippet(msg.media, own);

        const updated = {
          ...c,
          messages: [
            ...c.messages,
            {
              id: msg._id.toString(),
              own,
              text: msg.text,
              time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              media: msg.media,
            },
          ],
          snippet: mediaSnippet || msg.text || c.snippet,
          time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          lastActivity: new Date(msg.createdAt).getTime(),
          // increment unread if not active and message is from partner
          unread: (!fromMe && activeId !== convId) ? (c.unread || 0) + 1 : c.unread || 0,
        };

        const next = [...p];
        next[idx] = updated;

        // sort: unread first, then by lastActivity desc
        next.sort((a, b) => {
          if ((a.unread || 0) > 0 && (b.unread || 0) === 0) return -1;
          if ((b.unread || 0) > 0 && (a.unread || 0) === 0) return 1;
          return (b.lastActivity || 0) - (a.lastActivity || 0);
        });

        return next;
      });

      // If this conversation is currently open and incoming from partner, mark seen immediately
      if (!fromMe && activeId === convId) {
        try {
          await backend.post("/api/chat/messages/mark-seen", {
            conversationId: convId,
            userId: user._id.toString(),
          });
          setContacts((p) => p.map((c) => (c.conversationId === convId ? { ...c, unread: 0 } : c)));
        } catch (err) {
          console.error("mark-seen (socket) failed:", err?.message);
        }
      }
    };

    socket.on("message", handler);
    return () => socket.off("message", handler);
  }, [user._id, activeId]);

  /* Send message (keeps URL in sync via openChat) */
  const sendMessage = useCallback(
    async (id, text, media = null) => {
      const contact = contacts.find((c) => c.id === id);
      if (!contact) {
        setError("Contact not found");
        setSnack({ open: true, message: "Contact not found", severity: "error" });
        return;
      }

      let conversationId = contact.conversationId;
      if (!conversationId) {
        // openChat ensures URL param sync & stub sidebar contact
        await openChat(id, null);
        const updated = contacts.find((c) => c.id === id);
        conversationId = updated?.conversationId;
        if (!conversationId) return;
      }

      try {
        setSending(true);
        const tempMsgId = `tmp-${Date.now()}`;

        // optimistic
        setContacts((p) =>
          p.map((c) =>
            c.conversationId === conversationId
              ? {
                  ...c,
                  messages: [
                    ...c.messages,
                    {
                      id: tempMsgId,
                      own: true,
                      text: text || "",
                      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                      media,
                    },
                  ],
                  snippet: media ? classifyMediaSnippet(media, true) : (text || ""),
                  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  lastActivity: Date.now(),
                }
              : c
          )
        );

        const body = { conversationId: conversationId.toString(), sender: user._id.toString(), text: text || "", media };
        const { data } = await backend.post("/api/chat/messages", body);
        const msg = data.data;

        // replace optimistic w/ server
        setContacts((p) =>
          p.map((c) => {
            if (c.conversationId !== conversationId) return c;
            const withoutTemp = c.messages.filter((m) => m.id !== tempMsgId);
            if (withoutTemp.some((m) => m.id === msg._id.toString())) return c;

            const next = {
              ...c,
              messages: [
                ...withoutTemp,
                {
                  id: msg._id.toString(),
                  own: true,
                  text: msg.text,
                  time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  media: msg.media,
                },
              ],
              snippet: msg.media ? classifyMediaSnippet(msg.media, true) : (msg.text || c.snippet),
              time: new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              lastActivity: new Date(msg.createdAt).getTime(),
            };
            return next;
          })
        );

        setError(null);
      } catch (err) {
        console.error("Send message error:", err.response?.data || err.message);
        setError("Failed to send message: " + (err.response?.data?.message || err.message));
        setSnack({ open: true, message: "Failed to send message", severity: "error" });

        // rollback
        setContacts((p) =>
          p.map((c) =>
            c.conversationId === conversationId
              ? { ...c, messages: c.messages.filter((m) => !m.id?.toString?.().startsWith("tmp-")) }
              : c
          )
        );
      } finally {
        setSending(false);
      }
    },
    [user._id, contacts, openChat]
  );

  const activeContact = contacts.find((c) => c.conversationId === activeId) || null;

  // contacts to show: already filtered to booked; include active if not present
  const contactsToShow = (() => {
    if (!activeContact) return contacts;
    const exists = contacts.some((c) => c.id === activeContact.id);
    return exists ? contacts : [activeContact, ...contacts];
  })();

  return (
    <div className={Classes.hero}>
      <div className={Classes.header}>
        <div>
          <h3 className={Classes.title3}>Messages</h3>
          <h5 className={Classes.text}>Chat with your mentees (booked & active).</h5>
        </div>
      </div>

      <div className={Classes.wrapper} style={{ position: "relative" }}>
        {/* Initial load overlay */}
        {initializing && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(255,255,255,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1
          }}>
            <CircularProgress />
          </div>
        )}

        <AnimatePresence initial={false} mode="wait">
          {(!isMobile || activeId === null) && (
            <ContactList
              key="contacts"
              list={contactsToShow}
              activeId={activeId}
              onSelect={openChat}
              isMobile={isMobile}
              loading={loadingConvos && !forcingOpen}
            />
          )}
        </AnimatePresence>

        <AnimatePresence initial={false} mode="wait">
          {(!isMobile || activeId !== null) && (
            <ChatWindow
              key={activeContact ? `chat-${activeContact.id}` : "blank"}
              contact={activeContact}
              send={sendMessage}
              back={() => setActiveId(null)}
              isMobile={isMobile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              sending={sending}
            />
          )}
        </AnimatePresence>
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
